import { BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import CustomConfirmer from 'components/customConfirmer/customConfirmer';
import ErrorMessage from 'components/errorMessage/errorMessage';
import { useNotificacao } from 'hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IReceberDadosGuiaRecolhimentoResumo } from 'interfaces/IGuiaRecolhimento';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import { useEffect, useState } from 'react';
import { Button, Modal, Radio } from 'react-dsgov';
import { Tooltip } from 'react-tooltip';
import { formatarCompetencia } from 'utils/formatCompetencia';
import { formatCurrency } from 'utils/formatCurrency';
import { formatarDataISOpBR } from 'utils/formatDate';
import { tratarMensagemErro } from 'utils/tratarMensagemErro';
import pixIcon from '../../assets/pix_icon_100.png';
import styles from './modalOpcoesPagamento.module.scss';

interface ModalOpcoesPagamentoProps {
  readonly title: string;
  readonly isModalOpened: boolean;
  readonly setIsModalOpened: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setStep: React.Dispatch<React.SetStateAction<number>>;
  readonly step: number;
  readonly opcaoPagamento: string;
  readonly setOpcaoPagamento: React.Dispatch<React.SetStateAction<string>>;
  readonly maxStep: number;
}

function ModalOpcoesPagamento({
  title,
  isModalOpened,
  setIsModalOpened,
  setStep,
  step,
  opcaoPagamento,
  setOpcaoPagamento,
  maxStep,
}: ModalOpcoesPagamentoProps) {
  const [selectedRadio, setSelectedRadio] = useState<string>(opcaoPagamento);
  const [isModalPagTesouroOpened, setIsModalPagTesouroOpened] = useState<boolean>(false);
  const [proximaUrlPagTesouro, setProximaUrlPagTesouro] = useState<string>('');
  const [isOpcaoPgtoBoletoPermitida, setIsOpcaoPgtoBoletoPermitida] = useState<boolean>(false);
  const [isReportOverlayOpen, setIsReportOverlayOpen] = useState<boolean>(false);
  const [valorMinimoBoleto, setValorMinimoBoleto] = useState<number>(50);
  const [loading, setLoading] = useState<boolean>(false);
  const VALOR_MINIMO_GUIA = 0;

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const { getResumo, getEmitirGru, getImprimirBoleto, getValorMinimoBoleto } = useBackEndRequests();
  const mostrarNotificacao = useNotificacao();

  const {
    data: resumoData,
    isLoading,
    refetch,
    error,
  } = useQuery<IReceberDadosGuiaRecolhimentoResumo, IErrorWithSentryId>({
    queryKey: ['getResumoQuery', guiaRecolhimentoId],
    queryFn: () => getResumo(guiaRecolhimentoId),
    refetchOnWindowFocus: false,
    enabled: isModalOpened,
  });

  const {
    data: valorMinimoBoletoData,
    isLoading: isLoadingValorMinimoBoleto,
    refetch: refetchValorMinimoBoleto,
    error: errorValorMinimoBoleto,
  } = useQuery<{ valorMinimoBoleto: number }, IErrorWithSentryId>({
    queryKey: ['getValorMinimoBoletoQuery'],
    queryFn: () => getValorMinimoBoleto(),
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const imprimirBoleto = async () => {
    if (isReportOverlayOpen === false && resumoData) {
      processarReport(getImprimirBoleto, 'boleto');
    }
  };

  const processarReport = async (fnGetReport: (guiaRecolhimentoId: string) => Promise<Blob>, tipoArquivo: string) => {
    setIsReportOverlayOpen(true);

    await fnGetReport(guiaRecolhimentoId)
      .then((retorno) => {
        prepararReportDownload(retorno, tipoArquivo);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível baixar o arquivo solicitado neste momento',
          ...(err && { complementoSentryId: err }),
        });
      })
      .finally(() => {
        setIsReportOverlayOpen(false);
      });
  };

  const prepararReportDownload = (retorno: Blob, tipoArquivo: string) => {
    const boletoImpresso = new Blob([retorno], { type: 'application/pdf' });

    const url = URL.createObjectURL(boletoImpresso);

    const link = document.createElement('a');
    link.href = url;
    window.open(url, '_blank');
  };

  const confirmarGerarArrecadacao = async () => {
    setLoading(true);
    await getEmitirGru(guiaRecolhimentoId, selectedRadio)
      .then((res) => {
        setOpcaoPagamento(selectedRadio);
        if (selectedRadio === 'BOLETO') {
          setIsModalOpened(false);
          setStep(step < maxStep ? step + 1 : step);
          imprimirBoleto();
        } else {
          setProximaUrlPagTesouro(res.urlPagamento);
          setIsModalPagTesouroOpened(true);
        }
      })
      .catch((err: IErrorWithSentryId) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: tratarMensagemErro('getEmitirGru', err, 'Erro ao emitir guia de recolhimento.'),
          complementoSentryId: err,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fecharModalPagtesouro = async () => {
    setIsModalPagTesouroOpened(false);
    setStep(step < maxStep ? step + 1 : step);
    setIsModalOpened(false);
  };

  useEffect(() => {
    if (valorMinimoBoletoData) {
      setValorMinimoBoleto(valorMinimoBoletoData.valorMinimoBoleto);
    }
  }, [valorMinimoBoletoData]);

  useEffect(() => {
    if (resumoData) {
      if (resumoData.resumoTotal.dataVencimentoBoleto && resumoData.resumoTotal.cfemNoMesAtual > valorMinimoBoleto) {
        setIsOpcaoPgtoBoletoPermitida(true);
      } else {
        setIsOpcaoPgtoBoletoPermitida(false);
      }
    }
  }, [resumoData, valorMinimoBoleto]);

  useEffect(() => {
    if (isModalPagTesouroOpened) {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/3.6.6/iframeResizer.min.js';

      script.onload = () => {
        (window as any).iFrameResize({ heightCalculationMethod: 'documentElementOffset' }, '.iframe-epag');
      };

      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isModalPagTesouroOpened]);

  const getBoletoTooltipText = () => {
    return (
      <>
        O pagamento via boleto não está disponível pois o valor da CFEM a recolher é igual ou inferior a{' '}
        {formatCurrency(valorMinimoBoleto)}.
      </>
    );
  };

  const getIsConfirmarDisable = () => {
    return selectedRadio == '' || !resumoData || resumoData.resumoTotal.cfemNoMesAtual <= VALOR_MINIMO_GUIA;
  };

  const getConfirmarButtonToolTipText = () => {
    if (selectedRadio == '') {
      return 'Selecione uma forma de pagamento para avançar.';
    }
    return 'Não é possível gerar uma guia de recolhimento com valor zerado, favor corrigir o preenchimento.';
  };

  const conteudoDadosPagamentoDatasVencimento = () => {
    if (!resumoData) return null;

    if (
      resumoData.resumoTotal.dataVencimento === resumoData.resumoTotal.dataVencimentoBoleto ||
      !resumoData.resumoTotal.dataVencimentoBoleto
    )
      return (
        <div className={styles.dadosPagamentoContainerRow}>
          <span className={styles.label}>Data vencimento: </span>
          <span className={styles.value}>{formatarDataISOpBR(resumoData.resumoTotal.dataVencimento)}</span>
        </div>
      );

    return (
      <div className={styles.dadosPagamentoDatasVencimentoContainer}>
        <div className={styles.dadosPagamentoContainerRow}>
          <span className={styles.label}>Data vencimento (Pix ou Cartão de Crédito): </span>
          <span className={styles.value}>{formatarDataISOpBR(resumoData.resumoTotal.dataVencimento)}</span>
        </div>
        <div className={styles.dadosPagamentoContainerRow}>
          <span className={styles.label}>Data vencimento (Boleto): </span>
          <span className={styles.value}>{formatarDataISOpBR(resumoData.resumoTotal.dataVencimentoBoleto)}</span>
        </div>
      </div>
    );
  };

  const conteudoDadosPagamento = () => {
    if (isLoading) {
      return (
        <div data-testid='loading-container'>
          <BrLoading large />
        </div>
      );
    }

    if (error) {
      return (
        <ErrorMessage
          errorMessage='Erro ao carregar dados do resumo.'
          error={error}
          errorTitle='Erro ao carregar resumo'
          onClickFunction={() => refetch()}
          imageWidth={100}
        />
      );
    }

    if (resumoData) {
      return (
        <>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Período de competência: </span>
            <span className={styles.value}>{formatarCompetencia(resumoData.resumoTotal.periodoApuracao)}</span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>CFEM apurada no mês de ocorrência do fato gerador: </span>
            <span className={styles.value}>{formatCurrency(resumoData.resumoTotal.cfemMesOcorrenciaFatoGerador)}</span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Correção monetária: </span>
            <span className={styles.value}>{formatCurrency(resumoData.resumoTotal.correcaoMonetaria)}</span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Juros: </span>
            <span className={styles.value}>{formatCurrency(resumoData.resumoTotal.juros)}</span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Multa: </span>
            <span className={styles.value}>{formatCurrency(resumoData.resumoTotal.multas)}</span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>CFEM a Recolher: </span>
            <span className={styles.value}>{formatCurrency(resumoData.resumoTotal.cfemNoMesAtual)}</span>
          </div>

          {conteudoDadosPagamentoDatasVencimento()}

          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Data pagamento: </span>
            <span className={styles.value}>
              {resumoData.resumoTotal.dataPagamento === null
                ? '-'
                : formatarDataISOpBR(resumoData.resumoTotal.dataPagamento)}
            </span>
          </div>
          <div className={styles.dadosPagamentoContainerRow}>
            <span className={styles.label}>Dias em atraso: </span>
            <span className={styles.value}>{resumoData.resumoTotal.diasEmAtraso}</span>
          </div>
        </>
      );
    }
    return <span>Nenhum dado disponível.</span>;
  };

  const opcoesPagamentoHTML = () => {
    if (isLoadingValorMinimoBoleto) {
      return (
        <div data-testid='loading-container'>
          <BrLoading large />
        </div>
      );
    }

    if (errorValorMinimoBoleto) {
      return (
        <ErrorMessage
          errorMessage='Erro ao carregar dados do valor mínimo para geração de boleto.'
          error={errorValorMinimoBoleto}
          errorTitle='Erro ao carregar valor mínimo para geração de boleto'
          onClickFunction={() => refetchValorMinimoBoleto()}
          imageWidth={100}
        />
      );
    }

    if (valorMinimoBoletoData) {
      return (
        <div className={styles.itensContainer}>
          <Radio
            checked={selectedRadio === 'PIX'}
            id='radio-pix'
            label={
              <div className={styles.radioContent}>
                <img alt='Pix' src={pixIcon} className={styles.icon} />
                <span className={styles.radioLabel}>Pix</span>
              </div>
            }
            data-testid='radio-pix'
            value='radio-pix'
            name='radio-items'
            className={`${styles.radio} ${selectedRadio === 'PIX' ? styles.selected : ''}`}
            onClick={() => setSelectedRadio('PIX')}
          />
          <div data-tooltip-id='radio-boleto-tooltip'>
            <Radio
              checked={selectedRadio === 'BOLETO'}
              id='radio-boleto'
              label={
                <div className={styles.radioContent}>
                  <i className={`fas fa-solid fa-barcode fa-2x ${styles.icon}`} />
                  <span className={styles.radioLabel}>Boleto</span>
                </div>
              }
              data-testid='radio-boleto'
              value='radio-boleto'
              name='radio-items'
              className={`${styles.radio} ${selectedRadio === 'BOLETO' ? styles.selected : ''}`}
              onClick={() => setSelectedRadio('BOLETO')}
              disabled={!isOpcaoPgtoBoletoPermitida}
            />
          </div>
          <Tooltip
            opacity={1}
            id='radio-boleto-tooltip'
            place='bottom'
            className={styles.tooltip}
            variant='warning'
            disableTooltip={() => isOpcaoPgtoBoletoPermitida}
            delayHide={100}
          >
            <div className={styles.tooltipText}>{getBoletoTooltipText()}</div>
          </Tooltip>
          <Radio
            checked={selectedRadio === 'CARTAO_CREDITO'}
            id='radio-cartao-credito'
            label={
              <div className={styles.radioContent}>
                <i className={`far fa-credit-card fa-2x ${styles.icon}`} />
                <span className={styles.radioLabel}>Cartão de Crédito</span>
              </div>
            }
            data-testid='radio-cartao-credito'
            value='radio-cartao-credito'
            name='radio-items'
            className={`${styles.radio} ${selectedRadio === 'CARTAO_CREDITO' ? styles.selected : ''}`}
            onClick={() => setSelectedRadio('CARTAO_CREDITO')}
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className={styles.modalOpcoesPagamentoContainer}>
        {loading && (
          <div className={styles.loadingContainer}>
            <BrLoading
              large
              label={
                selectedRadio === 'BOLETO'
                  ? 'Gerando o Boleto da sua Guia de Recolhimento...'
                  : 'Comunicando com o PagTesouro para dar continuidade ao seu pagamento...'
              }
            />
          </div>
        )}
        <div>
          <Modal
            title={title}
            useScrim
            showCloseButton
            onCloseButtonClick={() => {
              setIsModalOpened(false);
            }}
            modalOpened={isModalOpened}
            className={styles.modal + ' ' + 'large'}
          >
            <div className={styles.contentContainer}>
              <div className={styles.dadosPagamentoContainer}>
                <div className={styles.subtitleContainer}>
                  <span className={styles.subtitle}>Dados da Guia de recolhimento</span>
                </div>
                {conteudoDadosPagamento()}
              </div>
              <div className={styles.opcoesPagamentoContainer}>
                <div className={styles.subtitleContainer}>
                  <span className={styles.subtitle}>Selecione a forma de pagamento para continuar</span>
                </div>
                {opcoesPagamentoHTML()}
              </div>
            </div>
            <div className={styles.containerTextoAviso}>
              <span className={styles.textoAuxiliar}>
                Ao confirmar a emissão da GRU, não será possível escolher outra forma de pagamento para a mesma guia.
              </span>
            </div>

            <div className={styles.botao}>
              <Button
                secondary
                small
                m={2}
                onClick={() => {
                  setIsModalOpened(false);
                }}
              >
                Cancelar
              </Button>
              <div data-tooltip-id='gerar-arrecadacao-tooltip'>
                <CustomConfirmer disabled={getIsConfirmarDisable()} asyncCall={() => confirmarGerarArrecadacao()}>
                  Confirmar
                </CustomConfirmer>
              </div>
              <Tooltip
                opacity={1}
                id='gerar-arrecadacao-tooltip'
                place='bottom'
                className={styles.tooltip}
                variant='warning'
                disableTooltip={() => !getIsConfirmarDisable()}
                delayHide={100}
              >
                <div className={styles.tooltipText}>{getConfirmarButtonToolTipText()}</div>
              </Tooltip>
            </div>
          </Modal>
        </div>
      </div>
      <div className={styles.iframeContainer}>
        <Modal
          useScrim
          showCloseButton
          onCloseButtonClick={() => {
            fecharModalPagtesouro();
          }}
          modalOpened={isModalPagTesouroOpened}
          className={styles.modal + ' ' + 'large'}
        >
          <iframe
            className={styles['iframe-epag'] + ' ' + 'iframe-epag'}
            src={proximaUrlPagTesouro}
            scrolling='no'
          ></iframe>
        </Modal>
      </div>
    </>
  );
}

export default ModalOpcoesPagamento;
